import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import LevelComponent from "./LevelComponent";
import { useHistory } from "react-router";
import { loadConfigurationByLevel } from "../../Services/ConfiguratorService";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { darkBlack, textBlack } from "../../Constants/Colors";

export const useStyles = makeStyles((theme) => ({
  container: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      alignItems: "start",
    },
  },
  levelsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      width: "100%",
      alignItems: "center",
    },
  },
  listItem: {
    display: "list-item",
    marginLeft: 15,
    listStyle: "disc",
    paddingLeft: theme.spacing(1),
    paddingTop: 0,
    paddingBottom: 0,
    "& .MuiTypography-displayBlock": {
      fontSize: "14px",
      lineHeight: "26px",
      color: darkBlack,
      letterSpacing: "0.7px",
    },
    [theme.breakpoints.down("sm")]: {
      "& .MuiTypography-displayBlock": {
        fontSize: "11px",
        lineHeight: "100%",
        letterSpacing: "0.7px",
        color: darkBlack,
        "& .MuiTypography-displayBlock: hover": {
          color: "white",
        },
      },
    },
  },
  overviewTitle: {
    marginTop: "7.6vh",
    marginBottom: 8,
    fontWeight: 900,
    fontSize: "27px",
    lineHeight: "103%",
    color: textBlack,
    [theme.breakpoints.down("sm")]: {
      margin: "31px 49px 0px 5%",
    },
  },
  subtitle: {
    fontSize: "20px",
    fontWeight: 400,
    lineHeight: "30px",
    marginBottom: "4.8vh",
    color: textBlack,
    [theme.breakpoints.down("sm")]: {
      lineHeight: "100%",
      margin: "14px 49px 5px 5%",
    },
  },
  info: {
    marginBottom: 20,
  },
  levelInfoText: {
    fontSize: "14px",
    paddingTop: "10px",
    letterSpacing: "0.7px",
    lineHeight: "120%",
    color: darkBlack,
    [theme.breakpoints.down("sm")]: {
      fontSize: "11px",
    },
  },
  header: {
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-end",
      paddingRight: 0,
    },
  },
}));

const LevelSelectionComponent = () => {
  const classes = useStyles();
  const history = useHistory();

  const level1Info = t("levelOverview.overview.level1Info").split('|');
  const level2Info = t("levelOverview.overview.level2Info").split('|');
  const level3Info = t("levelOverview.overview.level3Info").split('|');

  const itemIdentifier = useSelector((state) =>
    get(state, "configurator.configuration.item.identifier", null)
  );
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <Box className={classes.container}>
      <Typography className={classes.overviewTitle}>
        {t("levelOverview.overview.title")}
      </Typography>
      <Typography className={classes.subtitle}>
        {t("levelOverview.overview.subTitle")}
      </Typography>
      <Box className={classes.levelsContainer}>
        <LevelComponent
          title={t("levelOverview.overview.level1Title")}
          levelInfo={
            level1Info.length > 1 ? (
              <List>
                {level1Info.map((step, key) => (
                  <ListItem key={key} className={classes.listItem}>
                    <ListItemText primary={step} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography className={classes.levelInfoText}>
                {level1Info.toString()}
              </Typography>
            )
          }
          minOrder={t("levelOverview.overview.level1MinOrder")}
          deliveryTime={t("levelOverview.overview.level1DeleveryTime")}
          onLevelBtnClick={async () => {
            history.push({
              pathname: "/identifier:" + itemIdentifier,
              search: window.location.search,
            });
            await loadConfigurationByLevel(itemIdentifier, 1);
          }}
          imageURL="/images/level_images/level_1.jpg "
        />
        <LevelComponent
          title={t("levelOverview.overview.level2Title")}
          levelInfo={
            level2Info.length > 1 ? (
              <List>
                {level2Info.map((step, key) => (
                  <ListItem key={key} className={classes.listItem}>
                    <ListItemText
                      style={{
                        fontSize: "11px",
                      }}
                      primary={step}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography className={classes.levelInfoText}>
                {level2Info.toString()}
              </Typography>
            )
          }
          minOrder={t("levelOverview.overview.level2MinOrder")}
          deliveryTime={t("levelOverview.overview.level2DeleveryTime")}
          onLevelBtnClick={async () => {
            history.push({
              pathname: "/identifier:" + itemIdentifier,
              search: window.location.search,
            });
            await loadConfigurationByLevel(itemIdentifier, 2);
          }}
          imageURL="/images/level_images/level_2.jpg "
        />
        <LevelComponent
          title={t("levelOverview.overview.level3Title")}
          levelInfo={
            level3Info.length > 1 ? (
              <List>
                {level3Info.map((step, key) => (
                  <ListItem key={key} className={classes.listItem}>
                    <ListItemText primary={step} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography className={classes.levelInfoText}>
                {level3Info.toString()}
              </Typography>
            )
          }
          minOrder={t("levelOverview.overview.level3MinOrder")}
          deliveryTime={t("levelOverview.overview.level3DeleveryTime")}
          onLevelBtnClick={async () => {
            history.push({
              pathname: "/identifier:" + itemIdentifier,
              search: window.location.search,
            });
            await loadConfigurationByLevel(itemIdentifier, 3);
          }}
          imageURL="/images/level_images/level_3.jpg "
        />
      </Box>
    </Box>
  );
};

export default LevelSelectionComponent;
